import { format, isToday, isTomorrow, isYesterday, parseISO } from 'date-fns'

import i18nPlugin from '@/plugins/i18n'

import { type FormatFn } from './Format.types'
const i18n = i18nPlugin.global

const DATE_FORMAT = 'MMM dd, yyyy'

export const DateFormatter: FormatFn<string> = (value) => {
  const parsedDate = parseISO(value)
  return format(parsedDate, DATE_FORMAT)
}

export const DateFormatterTabular: FormatFn<string> = (value) => {
  const parsedDate = parseISO(value)
  return format(parsedDate, 'MM/dd/yyyy')
}

export const DateFormatterWithDailyContext: FormatFn<string> = (value) => {
  const parsedDate = parseISO(value)

  if (isToday(parsedDate)) {
    return i18n.t('formatters.date.today')
  }

  if (isTomorrow(parsedDate)) {
    return i18n.t('formatters.date.tomorrow')
  }

  if (isYesterday(parsedDate)) {
    return i18n.t('formatters.date.yesterday')
  }

  return format(parsedDate, DATE_FORMAT)
}

export interface DateFormatterOptions {
  /**
   * display - Default display value
   * display-context - Falls back to default context but includes `Today, Yesterday, and Tomorrow`
   * tabular - Formats the date in `MM/dd/yyyy` format
   */
  variant: 'display' | 'display-context' | 'tabular'
}

export const configureDateFormatter = (
  options: DateFormatterOptions = {
    variant: 'display',
  }
): FormatFn<string> => {
  switch (options.variant) {
    case 'tabular':
      return DateFormatterTabular
    case 'display-context':
      return DateFormatterWithDailyContext
    default:
      return DateFormatter
  }
}

/**
 * Formats a date from individual month, day, and optional year components.
 * @param month - Month number (1-12)
 * @param day - Day number (1-31)
 * @param year - Optional year number
 * @returns Formatted date string (e.g., "1/15" or "1/15/2024")
 */
export const formatDateParts = (
  month: number,
  day: number,
  year?: number | null
): string => {
  return year ? `${month}/${day}/${year}` : `${month}/${day}`
}
